import { createConsumer } from '@rails/actioncable';
import { emitter } from 'shared/helpers/mitt';
import logger from 'shared/helpers/logger';

export const INTERNAL_CHAT_EVENTS = {
  MESSAGE_CREATED: 'internal_chat.message_created',
  UNREAD_UPDATED: 'internal_chat.unread_updated',
  // Call lifecycle + WebRTC signaling — re-emitted for useInternalCall to consume.
  CALL_EVENT: 'internal_chat.call_event',
};

// Backend events (InternalCallsController + InternalChatChannel signaling) that
// the call layer cares about. Routed verbatim through the CALL_EVENT emitter.
const CALL_EVENT_NAMES = [
  'call.initiated',
  'call.answered',
  'call.declined',
  'call.ended',
  'call.signal.offer',
  'call.signal.answer',
  'call.signal.ice',
];

class InternalChatCable {
  constructor() {
    this.consumer = null;
    this.chatSubscription = null;
    this.accountSubscription = null;
    this.store = null;
    this.currentChatId = null;
    this.currentAccountId = null;
    this.currentUserId = null;
    this.pubsubToken = null;
    this.isInitialized = false;
    // typing indicator bookkeeping
    this.typingTimers = {};
    this.lastTypingSentAt = 0;
  }

  /**
   * Called once from App.vue (or equivalent) after auth.
   * pubsub_token is available at store.getters['auth/getCurrentUser'].pubsub_token
   * (the currentUser object on auth state has this field from the Rails API response).
   */
  init(store, pubsubToken) {
    if (this.isInitialized) return this;

    this.store = store;
    this.pubsubToken = pubsubToken;
    this.currentAccountId = store.getters.getCurrentAccountId;
    this.currentUserId = store.getters.getCurrentUserID;

    const { websocketURL = '' } = window.chatwootConfig || {};
    const wsUrl = websocketURL ? `${websocketURL}/cable` : undefined;

    this.consumer = createConsumer(wsUrl);
    this.isInitialized = true;

    // Subscribe to the account-level feed for unread badges
    this.subscribeToAccountFeed();

    return this;
  }

  /**
   * Subscribe to a specific chat channel to receive new messages in real time.
   * Unsubscribes from any previously watched chat first.
   */
  subscribeToChat(chatId) {
    if (!this.consumer || !this.store) {
      logger.warn('[InternalChatCable] Not initialized. Call init() first.');
      return;
    }

    if (this.chatSubscription) {
      this.unsubscribeFromChat();
    }

    this.currentChatId = chatId;

    this.chatSubscription = this.consumer.subscriptions.create(
      {
        channel: 'InternalChatChannel',
        chat_id: chatId,
        pubsub_token: this.pubsubToken,
        account_id: this.currentAccountId,
        user_id: this.currentUserId,
      },
      {
        connected: () => {
          logger.log('[InternalChatCable] Chat channel connected:', chatId);
        },
        disconnected: () => {
          logger.log('[InternalChatCable] Chat channel disconnected:', chatId);
        },
        rejected: () => {
          logger.warn(
            '[InternalChatCable] Chat subscription rejected for chat:',
            chatId
          );
        },
        received: data => this.onChatReceived(data),
      }
    );
  }

  /**
   * Unsubscribe from the current chat channel (called on chat change or logout).
   */
  unsubscribeFromChat() {
    // BUG-5 (audit 2026-06-16): null the ref BEFORE unsubscribing so a re-entrant
    // call (a chat switch and a component onBeforeUnmount firing in the same tick,
    // across InternalChatView + ChatView) becomes a no-op instead of sending a
    // second unsubscribe for an identifier the server already dropped (which logged
    // "Unable to find subscription"). Guard the unsubscribe itself defensively.
    const subscription = this.chatSubscription;
    this.chatSubscription = null;
    this.currentChatId = null;
    if (!subscription) return;

    try {
      subscription.unsubscribe();
    } catch (error) {
      logger.warn('[InternalChatCable] chat unsubscribe skipped:', error);
    }
  }

  /**
   * Subscribe to AccountInternalChatChannel for global unread badge updates.
   * This channel broadcasts whenever a message is sent in any chat where
   * the user is a participant (except messages sent by themselves).
   */
  subscribeToAccountFeed() {
    if (!this.consumer) return;

    this.accountSubscription = this.consumer.subscriptions.create(
      {
        channel: 'AccountInternalChatChannel',
        pubsub_token: this.pubsubToken,
        account_id: this.currentAccountId,
        user_id: this.currentUserId,
      },
      {
        connected: () => {
          logger.log('[InternalChatCable] Account feed connected');
        },
        rejected: () => {
          logger.warn('[InternalChatCable] Account feed subscription rejected');
        },
        received: data => this.onAccountFeedReceived(data),
      }
    );
  }

  onChatReceived(data) {
    if (!data || !data.event || !this.currentChatId) return;

    // Typing pings carry no `message` payload — handle before the guard below.
    if (data.event === 'typing') {
      this.handleTyping(data);
      return;
    }

    // Read receipts also carry no `message` payload.
    if (data.event === 'read') {
      this.store.commit('internalChats/SET_PARTICIPANT_READ', {
        chatId: data.chat_id,
        userId: data.user_id,
        lastReadAt: data.last_read_at,
      });
      return;
    }

    // Call lifecycle + WebRTC signaling — fan out to the call layer (useInternalCall).
    if (CALL_EVENT_NAMES.includes(data.event)) {
      emitter.emit(INTERNAL_CHAT_EVENTS.CALL_EVENT, data);
      return;
    }

    const { message } = data;
    if (!message) return;

    if (data.event === 'message.created') {
      this.store.commit('internalChats/ADD_MESSAGE', {
        chatId: this.currentChatId,
        message,
      });

      emitter.emit(INTERNAL_CHAT_EVENTS.MESSAGE_CREATED, {
        chatId: this.currentChatId,
        message,
      });
    } else if (data.event === 'message.updated') {
      // Backend broadcasts a partial payload (id/content/edited/edited_at);
      // UPDATE_MESSAGE merges it so sender/attachments/created_at are preserved.
      this.store.commit('internalChats/UPDATE_MESSAGE', {
        chatId: this.currentChatId,
        message,
      });
    } else if (data.event === 'message.deleted') {
      this.store.commit('internalChats/REMOVE_MESSAGE', {
        chatId: this.currentChatId,
        messageId: message.id,
      });
    }
  }

  /**
   * Tell the server the current user is typing in the active chat.
   * Throttled so we emit at most once every 2s while the user types.
   */
  sendTyping() {
    if (!this.chatSubscription) return;
    const now = Date.now();
    if (now - this.lastTypingSentAt < 2000) return;
    this.lastTypingSentAt = now;
    this.chatSubscription.perform('typing', {});
  }

  /**
   * Relay a WebRTC signaling message (offer/answer/ice) to the peer via the
   * active chat channel. action ∈ signal_offer | signal_answer | signal_ice.
   * Returns false when there is no live chat subscription to perform on.
   */
  performCallSignal(action, payload) {
    if (!this.chatSubscription) return false;
    this.chatSubscription.perform(action, payload);
    return true;
  }

  /**
   * Handle an incoming typing ping: record the typist in the store and
   * auto-clear them after 4s of silence (reset on every fresh ping).
   */
  handleTyping({ chat_id: chatId, user_id: userId, user_name: userName }) {
    if (!chatId || !userId || userId === this.currentUserId) return;

    this.store.commit('internalChats/SET_TYPING', { chatId, userId, userName });

    const key = `${chatId}:${userId}`;
    clearTimeout(this.typingTimers[key]);
    this.typingTimers[key] = setTimeout(() => {
      this.store.commit('internalChats/CLEAR_TYPING', { chatId, userId });
      delete this.typingTimers[key];
    }, 4000);
  }

  onAccountFeedReceived(data) {
    if (!data || !data.event) return;

    // Backend emits `unread_increment` (see InternalChatMessage#broadcast_unread_increment)
    if (data.event === 'unread_increment') {
      const { chat_id: chatId } = data;
      if (!chatId) return;

      // Only increment if this chat is not currently open
      const activeChat = this.store.getters['internalChats/getActiveChat'];
      if (activeChat && activeChat.id === chatId) return;

      this.store.commit('internalChats/INCREMENT_UNREAD', chatId);

      emitter.emit(INTERNAL_CHAT_EVENTS.UNREAD_UPDATED, { chatId });
    }
  }

  /**
   * Full cleanup — called on logout.
   */
  disconnect() {
    Object.values(this.typingTimers).forEach(clearTimeout);
    this.typingTimers = {};
    this.unsubscribeFromChat();

    if (this.accountSubscription) {
      this.accountSubscription.unsubscribe();
      this.accountSubscription = null;
    }

    if (this.consumer) {
      this.consumer.disconnect();
      this.consumer = null;
    }

    this.store = null;
    this.isInitialized = false;
  }
}

// Singleton — same pattern as pipelineCardCable
const internalChatCable = new InternalChatCable();

export default internalChatCable;
